import React, { FC, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { Query } from '@material-table/core';
import { MTable, Form } from '../../../../Components';
import { FormValues, FormFieldValueType } from '../../../../Components/Form/Form.types';
import { useFormField } from '../../../../Components/Form/FormField';
import { AptorApi, useAptorApi, useFetchSelectOptions } from '../../../../Api';
import {
  INamedEntity,
  IListResult,
  IGridQueryResult,
  IGridQueryRequest,
  OrderDirection,
} from '../../../../Api/AptorApi';
import { useStyles } from '../Organization/Organization.styles';

export interface IAspect extends INamedEntity {}

interface IProps {
  optionFetcher: () => Promise<IListResult<INamedEntity>>;
  dataFetcher: (request: IGridQueryRequest) => Promise<IGridQueryResult<IAspect>>;
  addNew: (api: AptorApi, data: FormValues) => Promise<void>;
  remove: (api: AptorApi, aspectid: number) => Promise<void>;
}

export const Aspects: FC<IProps> = ({ optionFetcher, dataFetcher, addNew, remove }) => {
  const { api, abortController } = useAptorApi();
  const tableRef = React.useRef<any>(null);
  const { formatMessage } = useIntl();
  const classes = useStyles();
  const [aspects, , , refetch] = useFetchSelectOptions(optionFetcher, abortController);

  const newAspectField = useFormField({
    label: formatMessage({ id: 'law-portal.manage.edit-law.addAspect' }),
    name: 'aspectId',
    type: 'single-select',
    options: aspects,
    required: true,
  });
  const setAspect = newAspectField.setValue;

  const handleDataFetch = useCallback(
    async (query: Query<IAspect>) => {
      return await dataFetcher({
        orderDirection: query.orderDirection === 'desc' ? OrderDirection.Descending : OrderDirection.Ascending,
        orderBy: query.orderBy !== undefined ? query.orderBy.field : 'name',
        page: query.page,
        pageSize: query.pageSize,
        search: query.search,
      } as IGridQueryRequest);
    },
    [dataFetcher],
  );

  const handleAddAspect = async (data: FormValues, api: AptorApi, onSuccess: () => void) => {
    await addNew(api, { ...data, aspects: [data.aspectId as FormFieldValueType] });
    if (api.abortController?.signal.aborted) {
      return;
    }
    onSuccess();
    (tableRef.current as any).onQueryChange();
    setAspect(undefined);
    refetch();
  };

  const removeAspect = (aspect: IAspect) => ({
    tooltip: formatMessage({ id: 'component.mtable.deleteTooltip' }),
    icon: 'delete',
    onClick: async () => {
      await remove(api, aspect.id);
      if (abortController.current.signal.aborted) {
        return;
      }
      (tableRef.current as any).onQueryChange();
      refetch();
    },
  });

  return (
    <>
      <Form
        className={classes.form}
        singleField={newAspectField}
        submit={handleAddAspect}
        submitLabel={formatMessage({ id: 'form.add' })}
      />

      <MTable
        // hideToolbar
        tableRef={tableRef}
        columns={[{ title: formatMessage({ id: 'organization.aspects' }), field: 'name' }]}
        data={handleDataFetch}
        actions={[removeAspect]}
        components={{}}
      />
    </>
  );
};
